import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarCheck2, Clock, Loader2, MapPin } from "lucide-react";
import DateTimeSlotPicker from "@/components/DateTimeSlotPicker";

function tomorrowLocalISO() {
  const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}T09:00:00`;
}

/**
 * Book Quote Appointment modal.
 *
 * On confirm this creates ONE deal (stage=enquiry) + ONE activity (type=meeting)
 * linked to that deal. The activity has `due_at` set to the appointment datetime
 * so it flows automatically into Daily Tasks + the Calendar.
 */
export default function BookQuoteAppointmentDialog({ open, onClose, client, onScheduled }) {
  const qc = useQueryClient();
  const [dueAt, setDueAt] = useState(tomorrowLocalISO());
  const [duration, setDuration] = useState("60"); // minutes
  const [fenceType, setFenceType] = useState("colorbond");
  const [siteAddress, setSiteAddress] = useState(
    client?.addresses?.[0] ? [client.addresses[0].line1, client.addresses[0].suburb].filter(Boolean).join(", ") : ""
  );
  const [notes, setNotes] = useState("");

  // Fence types
  const { data: fenceTypes = [] } = useQuery({
    queryKey: ["fence-types-book"],
    queryFn: async () => (await api.get("/fence-types")).data,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const dueLocal = new Date(dueAt);
      const dueISO = dueLocal.toISOString();
      const fenceLabel = (fenceTypes.find((f) => f.value === fenceType)?.label) || fenceType;

      // 1. Create the deal (enquiry stage)
      const dealPayload = {
        title: `${client.name} — ${fenceLabel} enquiry`,
        stage: "enquiry",
        company_id: client.id,
        fence_type: fenceType,
        value: 0,
        notes: notes || null,
      };
      const { data: deal } = await api.post("/deals", dealPayload);

      // 2. Create the appointment activity, linked to the deal
      const activityPayload = {
        type: "meeting",
        title: `Site quote — ${client.name}`,
        description: [
          `Client: ${client.name}`,
          siteAddress ? `Site: ${siteAddress}` : null,
          `Duration: ${duration} min`,
          notes ? `Notes: ${notes}` : null,
        ].filter(Boolean).join("\n"),
        due_at: dueISO,
        company_id: client.id,
        deal_id: deal.id,
        completed: false,
        priority: "high",
      };
      const { data: activity } = await api.post("/activities", activityPayload);

      return { deal, activity };
    },
    onSuccess: ({ deal, activity }) => {
      toast.success(`Quote appointment booked — added to Daily Tasks and Deals pipeline`);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["activities-today"] });
      onScheduled?.({ deal, activity });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Booking failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="book-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <CalendarCheck2 className="text-cyan-600" size={20} />
            Book Quote Appointment
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Creates a Deal (enquiry stage) + Daily Task + Calendar event, all linked to {" "}
            <b className="text-slate-700">{client?.name}</b>.
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Shared calendar + slot picker (iter-73). */}
          <DateTimeSlotPicker
            value={dueAt}
            onChange={setDueAt}
            testidPrefix="book"
          />

          {/* Duration */}
          <div>
            <Label className="stamp-label text-slate-600">Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="mt-1 h-10 rounded-sm w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Fence type */}
          <div>
            <Label className="stamp-label text-slate-600">Fence / Job type</Label>
            <Select value={fenceType} onValueChange={setFenceType}>
              <SelectTrigger data-testid="book-fence-type" className="mt-1 h-10 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fenceTypes.filter((f) => !["labour", "incidental"].includes(f.value)).map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Site address */}
          <div>
            <Label className="stamp-label text-slate-600">
              <MapPin size={11} className="inline mr-1" /> Site to visit
            </Label>
            <Input
              data-testid="book-site-address"
              value={siteAddress}
              onChange={(e) => setSiteAddress(e.target.value)}
              placeholder="Where should the quoter visit?"
              className="mt-1 h-10 rounded-sm"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="stamp-label text-slate-600">Site visit notes (internal)</Label>
            <Textarea
              data-testid="book-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Gate code, dog on premises, ring first, take drone…"
              className="mt-1 rounded-sm text-sm"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            data-testid="confirm-book-btn"
            onClick={() => submit.mutate()}
            disabled={submit.isPending || !dueAt}
            className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {submit.isPending ? (
              <><Loader2 size={14} className="animate-spin mr-1.5" /> Booking…</>
            ) : (
              <><Clock size={14} className="mr-1.5" /> Book appointment</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
